import React, { useState, useEffect, FormEvent, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import UserAvatarSvg from '../../assets/images/default-avatar.svg';
import validator from 'validator';
import RequestHelper from '../../utils/Request.Utils';
import { loginUserAction } from '../../store/user/actions';
import { loginUserDialogAction, registerUserDialogAction, forgotPasswordDialogAction } from '../../store/dialog/actions';
import { DialogNames } from '../../store/dialog/types';

type Props = {
  role: string
}

const SigninModal = ({ role }: Props) => {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    email: { value: '', validate: true, errorMsg: '' },
    password: { value: '', validate: true, errorMsg: '' },
  });
  const [alert, setAlert] = useState({
    isShow: false,
    status: 'danger',
    msg: ''
  });
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const { dialog } = useSelector((state: any) => ({
    dialog: state.dialog
  }));

  const checkEmailValidate = () => {
    if (formData.email.value.length === 0) {
      setFormData({
        ...formData,
        email: {
          value: formData.email.value,
          validate: false,
          errorMsg: 'Email is required!',
        },
      });
      return false;
    }
    if (validator.isEmail(formData.email.value)) {
      setFormData({
        ...formData,
        email: {
          value: formData.email.value,
          validate: true,
          errorMsg: '',
        },
      });
      return true;
    }
    setFormData({
      ...formData,
      email: {
        value: formData.email.value,
        validate: false,
        errorMsg: 'Not a valid email address!',
      },
    });
    return false;
  };

  const checkPasswordValidate = () => {
    setFormData({
      ...formData,
      password: {
        value: formData.password.value,
        validate: formData.password.value.length !== 0,
        errorMsg:
          formData.password.value.length === 0 ? 'Password required!' : '',
      },
    });
    return formData.password.value.length !== 0;
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      checkEmailValidate() &&
      checkPasswordValidate()
    ) {
      setLoading(true);
      RequestHelper
        .post('/users/login', {
          email: formData.email.value,
          password: formData.password.value
        })
        .then((res) => {
          if (!res.data.success) {
            setAlert({
              isShow: true,
              status: 'danger',
              msg: res.data.error
            });

            window.setTimeout(() => {
              setAlert({
                isShow: false,
                status: 'danger',
                msg: res.data.error
              });
            }, 2000)
          } else {
            const acceptedRoles = role.includes(',') ? role.split(',').map(val => val.trim()) : [role];
            if (acceptedRoles.includes('all') || acceptedRoles.includes(res.data.data.user.role)) {
              dispatch(loginUserAction(res.data.data));
              dispatch(loginUserDialogAction(false));

              if (role === 'broker')
                history.push('/dashboard');
            } else {
              setAlert({
                isShow: true,
                status: 'danger',
                msg: "Permission Denied!"
              });

              window.setTimeout(() => {
                setAlert({
                  ...alert,
                  isShow: false,
                });
              }, 2000)
            }
          }
          setLoading(false);
        })
        .catch((error) => {
          console.log(error);
        })
    }
  }

  const emailRef = useRef(null);

  return (
    <Modal
      show={dialog.isOpened && dialog.name === DialogNames.LOGIN_USER_DIALOG}
      onHide={() => { dispatch(loginUserDialogAction(false)) }}
      centered
      className="signin-modal"
      backdrop="static"
    >
      <Modal.Header className="flex-column">
        <h1>Welcome to your Virtual Tour Guide</h1>
        {/* <h2>A shared virtual experience</h2> */}
      </Modal.Header>
      <Modal.Body>
        {role === 'client' ?
          (<h1>Sign In as a <span className="user-type">{role}</span></h1>)
          :
          (<h1>Sign In</h1>)
        }
        <Form onSubmit={onSubmit} className="d-flex flex-column justify-content-center align-items-center">
          <img src={UserAvatarSvg} width="80" height="80" />
          <Form.Group controlId="signinForm.email" style={{ display: showForm ? undefined : 'none' }}>
            <Form.Control
              ref={emailRef}
              type="email"
              placeholder="Email"
              value={formData.email.value}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  email: {
                    value: e.target.value,
                    validate: true,
                    errorMsg: '',
                  }
                })
              }}
              isInvalid={!formData.email.validate}
            />
            <Form.Label>Email</Form.Label>
            <Form.Control.Feedback type="invalid">
              {formData.email.errorMsg}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="signinForm.password" style={{ display: showForm ? undefined : 'none' }}>
            <Form.Control
              type="password"
              placeholder="Password"
              value={formData.password.value}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  password: {
                    value: e.target.value,
                    validate: true,
                    errorMsg: '',
                  }
                })
              }}
              isInvalid={!formData.password.validate}
            />
            <Form.Label>Password</Form.Label>
            <Form.Control.Feedback type="invalid">
              {formData.password.errorMsg}
            </Form.Control.Feedback>
          </Form.Group>
          {!showForm && (
            <Form.Group onClick={() => {
              setFormData({
                email: { value: '', validate: true, errorMsg: '' },
                password: { value: '', validate: true, errorMsg: '' }
              });
              
              setTimeout(() => {
                setShowForm(true);
                setTimeout(() => {
                  if (emailRef.current && emailRef.current.focus) {
                    emailRef.current.focus();
                  }
                }, 300);
              }, 300);
            }}>
              <div style={{ paddingTop: 20 }}>
                <div style={{ marginBottom: 37 }}>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', lineHeight: '15px', fontSize: '0.9em' }}>Email</p>
                  <div style={{ height: 1, marginBottom: 10, width: '100%', backgroundColor: 'white' }} />
                </div>
                <div>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', lineHeight: '15px', fontSize: '0.9em' }}>Password</p>
                  <div style={{ height: 1, marginBottom: 10, width: '100%', backgroundColor: 'white' }} />
                </div>
              </div>
            </Form.Group>
          )}

          {loading ?
            <Button variant="primary" disabled>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              Loading...
            </Button>
            :
            <Button type="submit">
              Sign In
            </Button>
          }
          <a onClick={() => { dispatch(forgotPasswordDialogAction(true)) }}>Forgot password?</a>
          <div className="signin-btn d-flex justify-content-center align-items-center mt-3">
            <a onClick={() => { dispatch(registerUserDialogAction(true)) }}>Register</a>
          </div>
        </Form>
      </Modal.Body>
      <Alert variant="danger" show={alert.isShow}>{alert.msg}</Alert>
    </Modal>
  )
}

export default SigninModal;